import React from 'react';
import {
	Box,
	Text,
	VStack,
	HStack,
	Button,
	Input,
	FormControl,
	FormLabel,
	FormHelperText,
	Link,
	Icon,
	Accordion,
	AccordionItem,
	AccordionButton,
	AccordionPanel,
	AccordionIcon,
	SimpleGrid,
	Code,
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { FiShield, FiAlertCircle, FiExternalLink, FiSettings } from 'react-icons/fi';
import useAppToast from 'components/toast';
import Card from 'components/card/Card';
import { useConnectManualMutation } from 'api/metaWhatsappApi';
import { explainRefusal } from './_shared';

/**
 * Connect a WhatsApp number: a short guide and two fields.
 *
 * ★ IT ASKS FOR TWO IDS AND NOTHING ELSE.
 * The access token and the API URL belong to the WORKSPACE, not to a number:
 * one token authorises every number on the account, it is rotated in one place,
 * and it is the same pair WhatsApp already uses here. So it is set once in
 * Admin Settings → Secrets and read from there — this form never sees it. A
 * token field per number would mean the same secret pasted twice, stored twice,
 * and rotated in one of the two.
 *
 * ★ EVERY NUMBER ON THE ACCOUNT IS CONNECTED BY ONE SUBMISSION.
 * `#hydrateChannels` makes a channel for every number Meta lists on that WABA,
 * so the Phone number ID here only names which to set up first — it is not a
 * per-number registration. Telling a client to "come back for the next number"
 * would send them to re-submit a form that has already done the work.
 *
 * ★ NOTHING IS SAVED UNTIL META CONFIRMS. The connection service proves the
 * token really grants that account and that the number is on it, before writing
 * anything, and answers with a code that names what to fix.
 */

const SETTINGS_PATH = '/admin-setting/configuration';

const FIELDS = [
	{
		key: 'wabaId',
		label: 'WhatsApp Business Account ID',
		placeholder: 'e.g. 102290129340398',
		help: 'WhatsApp → API Setup, under "WhatsApp Business Account ID".',
	},
	{
		key: 'phoneNumberId',
		label: 'Phone number ID',
		placeholder: 'e.g. 106540352242922',
		help: 'The same page, under the number you want to connect.',
	},
];

const STEPS = [
	{
		title: 'Create a Meta app with WhatsApp',
		body: (
			<>
				Go to{' '}
				<Link href="https://developers.facebook.com/apps" isExternal color="text.accent">
					developers.facebook.com/apps <Icon as={FiExternalLink} boxSize="3" />
				</Link>
				, create a <b>Business</b> app (or open yours) and add the <b>WhatsApp</b> product. Complete
				business verification when Meta asks for it — messaging customers requires it.
			</>
		),
	},
	{
		title: 'Save your token and API URL once, in Settings',
		body: (
			<>
				In{' '}
				<Link href="https://business.facebook.com/settings/system-users" isExternal color="text.accent">
					Business settings → System users <Icon as={FiExternalLink} boxSize="3" />
				</Link>
				, add a system user with the <b>Admin</b> role, click <b>Add assets</b> and give it your app (full
				control) and your WhatsApp account. Then <b>Generate new token</b> with the permissions{' '}
				<Code fontSize="xs">whatsapp_business_messaging</Code> and{' '}
				<Code fontSize="xs">whatsapp_business_management</Code>, expiry <b>Never</b>.
				<br />
				<br />
				Put it in <b>Admin Settings → Secrets</b> under the service <Code fontSize="xs">whatsapp</Code>:{' '}
				<Code fontSize="xs">token</Code> and <Code fontSize="xs">api_url</Code> (
				<Code fontSize="xs">https://graph.facebook.com/v21.0</Code>). Once only — every number you connect
				uses it.
			</>
		),
	},
	{
		title: 'Copy the two IDs and connect',
		body: (
			<>
				<b>WhatsApp → API Setup</b> shows both. Paste them below. Nothing is saved until Meta confirms the
				token really grants that account and the number is on it; if something is wrong, the message says
				which.
				<br />
				<br />
				<b>Every number on that WhatsApp Business Account is connected at once</b> — the Phone number ID
				above just says which one to set up first. You only come back here to add a <b>different</b>{' '}
				WhatsApp Business Account.
			</>
		),
	},
	// ★ THE WEBHOOK AND ASSIGNMENT STEPS USED TO BE HERE TOO, and both were a
	// worse copy of a real thing on the same screen: WebhookCard carries the two
	// values AND says whether Meta has verified them, and every number card
	// carries its own people list. A step that only DESCRIBES a control the
	// reader can already see is a second source of truth that ages badly — this
	// one told them to look at "the card beside this one", which was only true
	// above 1280px.
];

const empty = () => ({ wabaId: '', phoneNumberId: '' });

export default function ConnectMetaAppForm({ onConnected, prefill = null }) {
	const [form, setForm] = React.useState(empty);
	const [failure, setFailure] = React.useState(null);
	const [connectManual, { isLoading }] = useConnectManualMutation();
	const notify = useAppToast();

	// ★ RECONNECTING IS THIS FORM WITH THE IDS ALREADY IN IT.
	// `connectManual` is idempotent on the phone number id: it re-reads the
	// workspace's CURRENT token, proves it against Meta, seals a new credential,
	// supersedes the old one and puts the account back to ACTIVE. That is the
	// whole of "re-auth", so there is no second endpoint and no second form —
	// which also means a reconnect cannot drift from a connect.
	const isReconnect = Boolean(prefill);
	const anchor = React.useRef(null);
	// Primitives, not the object: a parent re-render makes a new `prefill` every
	// time, and depending on it would retype the fields under the reader.
	const prefillWaba = prefill?.wabaId ?? null;
	const prefillPhone = prefill?.phoneNumberId ?? null;

	React.useEffect(() => {
		if (!prefillWaba && !prefillPhone) return;
		setFailure(null);
		setForm({ wabaId: prefillWaba ?? '', phoneNumberId: prefillPhone ?? '' });
		// The button that opens this sits further up a long page; without this the
		// click appears to do nothing.
		anchor.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
	}, [prefillWaba, prefillPhone]);

	const set = (key) => (e) => {
		setFailure(null);
		setForm((f) => ({ ...f, [key]: e.target.value }));
	};

	const complete = FIELDS.every((f) => form[f.key].trim().length > 0);

	const submit = async (e) => {
		e.preventDefault();
		if (!complete || isLoading) return;
		setFailure(null);
		try {
			const result = await connectManual({
				wabaId: form.wabaId.trim(),
				phoneNumberId: form.phoneNumberId.trim(),
			}).unwrap();

			const count = result?.channels?.length ?? 0;
			notify.success(
				isReconnect ? 'WhatsApp reconnected' : 'WhatsApp connected',
				result?.status === 'ACTIVE'
					? isReconnect
						? `Token accepted. ${count} number(s) working again.`
						: `${count} number(s) ready. Now point your Meta app at this CRM.`
					: 'Connected. Meta has not confirmed the subscription yet; it usually follows within a minute.',
			);
			setForm(empty());
			onConnected?.(result);
		} catch (err) {
			const payload = err?.data ?? {};
			setFailure({
				text: explainRefusal(payload),
				field: payload.field ?? null,
				// The one refusal with somewhere to go: the workspace has not set
				// its token yet, and the fix is a different screen.
				needsSettings: payload.error === 'WHATSAPP_NOT_CONFIGURED',
			});
		}
	};

	return (
		// Card does not forward refs — it is a plain function whose `...rest`
		// never receives one — so the scroll target is this wrapper.
		<Box ref={anchor}>
		<Card bg="bg.surface" borderWidth="1px" borderColor="border.default">
			<Box>
				{/* The panel above already says "Connect a WhatsApp number"; this card
				    is one of its two doors, so it names itself as that door. */}
				<Text fontSize="sm" fontWeight="bold" color="text.primary">
					{isReconnect ? 'Reconnect this account' : "Your Meta app's IDs"}
				</Text>
				<Text fontSize="xs" color="text.muted" mt="0.5" mb="3">
					{isReconnect
						? 'The ids are already filled in. Update the token in Admin Settings first — this reads it fresh.'
						: 'Two IDs from your Meta app. Your token is read from Admin Settings.'}
				</Text>

				<Accordion allowToggle defaultIndex={[0]} mb="4">
					{STEPS.map((step, i) => (
						<AccordionItem key={step.title} borderColor="border.subtle">
							<AccordionButton px="2" _hover={{ bg: 'bg.elevated' }}>
								<HStack flex="1" spacing="2" textAlign="left">
									<Box
										w="20px"
										h="20px"
										borderRadius="full"
										bg="accent.subtle"
										color="text.accent"
										fontSize="0.65rem"
										fontWeight="bold"
										display="flex"
										alignItems="center"
										justifyContent="center"
										flexShrink={0}
									>
										{i + 1}
									</Box>
									<Text fontSize="sm" color="text.primary">
										{step.title}
									</Text>
								</HStack>
								<AccordionIcon color="text.muted" />
							</AccordionButton>
							<AccordionPanel px="2" pb="3">
								<Text fontSize="xs" color="text.secondary" lineHeight="1.6">
									{step.body}
								</Text>
							</AccordionPanel>
						</AccordionItem>
					))}
				</Accordion>

				<form onSubmit={submit}>
					<VStack align="stretch" spacing="3">
						<SimpleGrid columns={{ base: 1, md: 2 }} spacing="3">
							{FIELDS.map((f) => (
								<FormControl key={f.key} isInvalid={failure?.field === f.key}>
									<FormLabel fontSize="xs" color="text.secondary" mb="1">
										{f.label}
									</FormLabel>
									<Input
										size="sm"
										bg="bg.input"
										inputMode="numeric"
										autoComplete="off"
										placeholder={f.placeholder}
										value={form[f.key]}
										onChange={set(f.key)}
									/>
									<FormHelperText fontSize="0.65rem" color="text.muted" mt="1">
										{f.help}
									</FormHelperText>
								</FormControl>
							))}
						</SimpleGrid>

						{failure && (
							<HStack
								spacing="2"
								align="start"
								px="3"
								py="2"
								bg="bg.subtle"
								borderLeftWidth="3px"
								borderLeftColor="danger"
								borderRadius="md"
							>
								<Icon as={FiAlertCircle} color="danger" mt="0.5" />
								<Box>
									<Text fontSize="sm" color="text.primary">
										{failure.text}
									</Text>
									{failure.needsSettings && (
										<Button
											as={RouterLink}
											to={SETTINGS_PATH}
											size="xs"
											variant="outline"
											mt="2"
											leftIcon={<Icon as={FiSettings} />}
										>
											Open Admin Settings
										</Button>
									)}
								</Box>
							</HStack>
						)}

						<HStack justify="space-between" align="center" pt="1">
							<HStack spacing="1.5">
								<Icon as={FiShield} color="text.muted" boxSize="3.5" />
								<Text fontSize="0.65rem" color="text.muted">
									Your token stays in Settings; it is never shown or typed here.
								</Text>
							</HStack>
							<Button type="submit" colorScheme="green" size="sm" isLoading={isLoading} isDisabled={!complete}>
								{isReconnect ? 'Verify & reconnect' : 'Verify & connect'}
							</Button>
						</HStack>
					</VStack>
				</form>
			</Box>
		</Card>
		</Box>
	);
}
